import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class TimeRangeValidators {

    /**
     * Custom validator for time range validation
     * Ensures that if both from and to times are provided, from time is before to time
     * If only one time is provided, it requires both times to be filled
     */
    static timeRange(fromFieldName: string, toFieldName: string): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const fromTime = group.get(fromFieldName)?.value;
            const toTime = group.get(toFieldName)?.value;
            const hasFromTime = !!fromTime;
            const hasToTime = !!toTime;

            // Clear any previous errors on individual controls
            const fromControl = group.get('from');
            const toControl = group.get('to');

            if (!hasFromTime && !hasToTime) {
                // Both empty - no errors
                TimeRangeValidators.clearControlError(fromControl, 'required');
                TimeRangeValidators.clearControlError(toControl, 'required');
                TimeRangeValidators.clearControlError(toControl, 'invalid');
                return null;
            } else if (hasFromTime && hasToTime) {
                // Both filled - validate range
                TimeRangeValidators.clearControlError(fromControl, 'required');
                TimeRangeValidators.clearControlError(toControl, 'required');

                const isValid = TimeRangeValidators.isValidTimeRange(fromTime, toTime);
                if (!isValid) {
                    TimeRangeValidators.setControlError(toControl, 'invalid', true);
                    return { timeRangeInvalid: true };
                } else {
                    TimeRangeValidators.clearControlError(toControl, 'invalid');
                }
                return null;
            } else {
                // One filled, one empty - require both
                if (hasFromTime && !hasToTime) {
                    TimeRangeValidators.setControlError(toControl, 'required', true);
                    return { timeRangeIncomplete: true };
                } else if (!hasFromTime && hasToTime) {
                    TimeRangeValidators.setControlError(fromControl, 'required', true);
                    return { timeRangeIncomplete: true };
                }
            }

            return null;
        };
    }

    /**
     * Helper method to set control errors
     */
    private static setControlError(control: AbstractControl | null, errorKey: string, errorValue: any): void {
        if (control) {
            const errors = control.errors || {};
            errors[errorKey] = errorValue;
            control.setErrors(errors);
            control.markAsTouched(); // Mark as touched to show errors
        }
    }

    /**
     * Helper method to clear specific control errors
     */
    private static clearControlError(control: AbstractControl | null, errorKey: string): void {
        if (control && control.errors) {
            const errors = { ...control.errors };
            delete errors[errorKey];
            const hasErrors = Object.keys(errors).length > 0;
            control.setErrors(hasErrors ? errors : null);
        }
    }

    /**
     * Validates if from time is before to time
     */
    private static isValidTimeRange(fromTime: string, toTime: string): boolean {
        const fromMinutes = TimeRangeValidators.timeToMinutes(fromTime);
        const toMinutes = TimeRangeValidators.timeToMinutes(toTime);

        return fromMinutes < toMinutes;
    }

    /**
     * Converts time string (HH:MM) to minutes since midnight
     */
    private static timeToMinutes(timeString: string): number {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    }
}
